import InfoIcon from '@material-design-icons/svg/filled/info.svg?react';
import RefreshIcon from '@material-design-icons/svg/filled/refresh.svg?react';
import { use, useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { DataTable } from '../components/common/DataTable';
import { FilterBar, SelectFilter } from '../components/common/FilterBar';
import { Pagination } from '../components/common/Pagination';
import { TypedStatusBadge } from '../components/common/StatusBadge';
import { Button } from '../components/ui/button';
import { TRPCContext } from '../contexts/TRPC';
import { usePageTitle } from '../hooks/usePageTitle';
import { isLeftResult, isRightResult } from '../lib/trpcTypeGuards';
import {
  type QueryLoadContext,
  usePaginatedQueryLoader,
} from '../hooks/usePaginatedQueryLoader';
import { useRequireAuth } from '../hooks/useRequireAuth';
import { captureAndReportError } from '../utils/errorReporting';

interface EmailDetail {
  id: string;
  recipientEmail: string;
  recipientName: string | null;
  senderEmail: string;
  senderName: string;
  subject: string;
  templateName: string;
  status: 'Pending' | 'Sending' | 'Sent' | 'Failed';
  sentAt: number | null;
  failedAt: number | null;
  failureReason: string | null;
  retryCount: number;
  brevoMessageId: string | null;
  created: number;
  alertId: string | null;
  dappId: string | null;
  releaseId: string | null;
  kybSubmissionId: string | null;
  publisherUserJoinId: string | null;
  htmlContent?: string;
  textContent?: string;
}

interface EmailDetailModalProps {
  email: EmailDetail | null;
  isOpen: boolean;
  onClose: () => void;
}

function EmailDetailModal({ email, isOpen, onClose }: EmailDetailModalProps) {
  const trpc = use(TRPCContext);
  const [fullEmail, setFullEmail] = useState<EmailDetail | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showHtml, setShowHtml] = useState(true);
  const latestDetailsRequestRef = useRef(0);

  const loadEmailDetails = useCallback(
    async (emailId: string) => {
      const requestId = latestDetailsRequestRef.current + 1;
      latestDetailsRequestRef.current = requestId;

      try {
        setIsLoading(true);
        const result = await trpc.client.getEmailDetails.query(emailId);

        if (requestId !== latestDetailsRequestRef.current) {
          return;
        }

        if (isRightResult(result)) {
          setFullEmail(result.right as EmailDetail);
        }
      } catch (error) {
        if (requestId !== latestDetailsRequestRef.current) {
          return;
        }

        captureAndReportError(error, {
          operation: 'admin.loadEmailDetails',
          component: 'EmailManagementPage',
          emailId,
        });
      } finally {
        if (requestId === latestDetailsRequestRef.current) {
          setIsLoading(false);
        }
      }
    },
    [trpc.client],
  );

  useEffect(() => {
    if (email && isOpen) {
      setShowHtml(true);
      void loadEmailDetails(email.id);
    } else {
      latestDetailsRequestRef.current += 1;
      setFullEmail(null);
      setIsLoading(false);
    }

    return () => {
      latestDetailsRequestRef.current += 1;
    };
  }, [email?.id, isOpen, loadEmailDetails]);

  if (!isOpen || !email) return null;

  const displayEmail = fullEmail || email;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-neutral-800 rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <h3 className="text-lg font-semibold text-white mb-4">Email Details</h3>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="text-sm text-neutral-400">Recipient</div>
              <div className="text-white">
                {displayEmail.recipientName || displayEmail.recipientEmail}
              </div>
              {displayEmail.recipientName && (
                <div className="text-sm text-neutral-400">
                  {displayEmail.recipientEmail}
                </div>
              )}
            </div>

            <div>
              <div className="text-sm text-neutral-400">Sender</div>
              <div className="text-white">{displayEmail.senderName}</div>
              <div className="text-sm text-neutral-400">
                {displayEmail.senderEmail}
              </div>
            </div>
          </div>

          <div>
            <div className="text-sm text-neutral-400">Subject</div>
            <div className="text-white">{displayEmail.subject}</div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="text-sm text-neutral-400">Template</div>
              <div className="text-white">{displayEmail.templateName}</div>
            </div>

            <div>
              <div className="text-sm text-neutral-400">Status</div>
              <TypedStatusBadge type="email" status={displayEmail.status} />
            </div>
          </div>

          {displayEmail.brevoMessageId && (
            <div>
              <div className="text-sm text-neutral-400">Brevo Message ID</div>
              <div className="text-white font-mono text-sm">
                {displayEmail.brevoMessageId}
              </div>
            </div>
          )}

          {displayEmail.failureReason && (
            <div>
              <div className="text-sm text-neutral-400">Failure Reason</div>
              <div className="text-red-400 text-sm">
                {displayEmail.failureReason}
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="text-sm text-neutral-400">Created</div>
              <div className="text-white">
                {new Date(displayEmail.created).toLocaleString()}
              </div>
            </div>

            {displayEmail.sentAt && (
              <div>
                <div className="text-sm text-neutral-400">Sent At</div>
                <div className="text-white">
                  {new Date(displayEmail.sentAt).toLocaleString()}
                </div>
              </div>
            )}

            {displayEmail.failedAt && (
              <div>
                <div className="text-sm text-neutral-400">Failed At</div>
                <div className="text-white">
                  {new Date(displayEmail.failedAt).toLocaleString()}
                </div>
              </div>
            )}
          </div>

          {displayEmail.retryCount > 0 && (
            <div>
              <div className="text-sm text-neutral-400">Retry Count</div>
              <div className="text-white">{displayEmail.retryCount}</div>
            </div>
          )}

          {isLoading ? (
            <div className="border-t border-neutral-700 pt-4">
              <div className="text-sm text-neutral-400">
                Loading email content…
              </div>
            </div>
          ) : fullEmail && (fullEmail.htmlContent || fullEmail.textContent) ? (
            <div className="border-t border-neutral-700 pt-4">
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm text-neutral-400">Email Content</div>
                {fullEmail.htmlContent && fullEmail.textContent && (
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setShowHtml(true)}
                      className={`text-xs px-2 py-1 rounded ${
                        showHtml
                          ? 'bg-teal-600 text-white'
                          : 'bg-neutral-700 text-neutral-400 hover:text-white'
                      }`}
                    >
                      HTML
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowHtml(false)}
                      className={`text-xs px-2 py-1 rounded ${
                        !showHtml
                          ? 'bg-teal-600 text-white'
                          : 'bg-neutral-700 text-neutral-400 hover:text-white'
                      }`}
                    >
                      Text
                    </button>
                  </div>
                )}
              </div>

              {showHtml && fullEmail.htmlContent ? (
                <div className="bg-neutral-900 rounded-lg p-4 border border-neutral-700">
                  <iframe
                    srcDoc={fullEmail.htmlContent}
                    className="w-full h-96 bg-white rounded"
                    title="Email HTML Content"
                    sandbox=""
                  />
                </div>
              ) : fullEmail.textContent ? (
                <div className="bg-neutral-900 rounded-lg p-4 border border-neutral-700">
                  <pre className="text-sm text-white whitespace-pre-wrap font-mono">
                    {fullEmail.textContent}
                  </pre>
                </div>
              ) : null}
            </div>
          ) : null}

          <div className="border-t border-neutral-700 pt-4">
            <div className="text-sm text-neutral-400 mb-2">
              Related Entities
            </div>
            <div className="space-y-1">
              {displayEmail.alertId && (
                <div className="text-sm">
                  <span className="text-neutral-400">Alert ID:</span>{' '}
                  <span className="text-white font-mono">
                    {displayEmail.alertId}
                  </span>
                </div>
              )}
              {displayEmail.dappId && (
                <div className="text-sm">
                  <span className="text-neutral-400">Dapp ID:</span>{' '}
                  <span className="text-white font-mono">
                    {displayEmail.dappId}
                  </span>
                </div>
              )}
              {displayEmail.releaseId && (
                <div className="text-sm">
                  <span className="text-neutral-400">Release ID:</span>{' '}
                  <span className="text-white font-mono">
                    {displayEmail.releaseId}
                  </span>
                </div>
              )}
              {displayEmail.kybSubmissionId && (
                <div className="text-sm">
                  <span className="text-neutral-400">KYB Submission ID:</span>{' '}
                  <span className="text-white font-mono">
                    {displayEmail.kybSubmissionId}
                  </span>
                </div>
              )}
              {!displayEmail.alertId &&
                !displayEmail.dappId &&
                !displayEmail.releaseId &&
                !displayEmail.kybSubmissionId && (
                  <div className="text-sm text-neutral-500">
                    No related entities
                  </div>
                )}
            </div>
          </div>
        </div>

        <div className="mt-6 flex justify-end">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </div>
  );
}

export function EmailManagementPage() {
  const { canRender } = useRequireAuth({
    requireRole: 'admin',
  });
  const trpc = use(TRPCContext);
  usePageTitle('Email Management | Publishing Portal');

  const [emails, setEmails] = useState<EmailDetail[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  const [statusFilter, setStatusFilter] = useState<
    'all' | 'Pending' | 'Sending' | 'Sent' | 'Failed'
  >('all');
  const [templateFilter, setTemplateFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [templates, setTemplates] = useState<string[]>([]);

  const [selectedEmail, setSelectedEmail] = useState<EmailDetail | null>(null);

  useEffect(() => {
    if (!canRender) return;
    let cancelled = false;
    void (async () => {
      try {
        const result = await trpc.client.getEmailTemplates.query(undefined);
        if (!cancelled && isRightResult(result)) {
          setTemplates(result.right.templates);
        }
      } catch (error) {
        if (cancelled) return;
        captureAndReportError(error, {
          operation: 'admin.loadEmailTemplates',
          component: 'EmailManagementPage',
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [canRender, trpc.client]);

  const loadEmails = useCallback(
    async ({ isCurrentRequest }: QueryLoadContext) => {
      try {
        const result = await trpc.client.listAllEmails.query({
          filters: {
            status: statusFilter,
            templateName: templateFilter === 'all' ? undefined : templateFilter,
            search: searchQuery || undefined,
            startDate: startDate || undefined,
            endDate: endDate || undefined,
          },
          pagination: {
            page: currentPage,
            limit: 20,
          },
        });

        if (!isCurrentRequest()) {
          return;
        }

        if (isRightResult(result)) {
          setEmails(result.right.emails);
          setTotalPages(result.right.pagination.totalPages);
          setTotal(result.right.pagination.total);
        } else if (isLeftResult(result)) {
          toast.error(result.left.message);
        }
      } catch (error) {
        if (!isCurrentRequest()) {
          return;
        }

        captureAndReportError(error, {
          operation: 'admin.loadEmails',
          component: 'EmailManagementPage',
          filters: {
            statusFilter,
            templateFilter,
            searchQuery,
            startDate,
            endDate,
          },
          page: currentPage,
        });
        toast.error('Failed to load emails');
      }
    },
    [
      currentPage,
      endDate,
      searchQuery,
      startDate,
      statusFilter,
      templateFilter,
      trpc.client,
    ],
  );

  const { reload: reloadEmails } = usePaginatedQueryLoader({
    enabled: canRender,
    setLoading: setIsLoading,
    loadPage: loadEmails,
  });

  const handleSearchChange = (value: string) => {
    setCurrentPage(1);
    setSearchQuery(value);
  };

  const handleStatusFilterChange = (value: string) => {
    setCurrentPage(1);
    setStatusFilter(value as 'all' | 'Pending' | 'Sending' | 'Sent' | 'Failed');
  };

  const handleTemplateFilterChange = (value: string) => {
    setCurrentPage(1);
    setTemplateFilter(value);
  };

  const handleStartDateChange = (value: string) => {
    setCurrentPage(1);
    setStartDate(value);
  };

  const handleEndDateChange = (value: string) => {
    setCurrentPage(1);
    setEndDate(value);
  };

  const clearDateFilters = () => {
    setCurrentPage(1);
    setStartDate('');
    setEndDate('');
  };

  const formatShortDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleDateString();
  };

  if (!canRender) {
    return null;
  }

  return (
    <>
      <div className="flex-1 overflow-y-auto p-4 md:p-8">
        <div className="max-w-7xl mx-auto">
          <div className="mb-6 space-y-4">
            <FilterBar
              searchValue={searchQuery}
              onSearchChange={handleSearchChange}
              searchPlaceholder="Search by email, name, or subject..."
              className="mb-0"
            >
              <SelectFilter
                value={statusFilter}
                onChange={handleStatusFilterChange}
                options={[
                  { value: 'all', label: 'All Statuses' },
                  { value: 'Sent', label: 'Sent' },
                  { value: 'Sending', label: 'Sending' },
                  { value: 'Pending', label: 'Pending' },
                  { value: 'Failed', label: 'Failed' },
                ]}
              />
              <SelectFilter
                value={templateFilter}
                onChange={handleTemplateFilterChange}
                options={[
                  { value: 'all', label: 'All Templates' },
                  ...templates.map((t) => ({ value: t, label: t })),
                ]}
              />
            </FilterBar>
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2">
                <input
                  type="date"
                  aria-label="Start date"
                  value={startDate}
                  onChange={(e) => handleStartDateChange(e.target.value)}
                  className="px-4 py-2.5 bg-neutral-900 text-white rounded-lg border border-neutral-700 hover:border-neutral-600 focus:border-teal-500 focus:ring-1 focus:ring-teal-500/20 focus:outline-none text-sm transition-colors [color-scheme:dark]"
                  placeholder="Start date"
                />
                <span className="text-neutral-400 text-sm">to</span>
                <input
                  type="date"
                  aria-label="End date"
                  value={endDate}
                  onChange={(e) => handleEndDateChange(e.target.value)}
                  className="px-4 py-2.5 bg-neutral-900 text-white rounded-lg border border-neutral-700 hover:border-neutral-600 focus:border-teal-500 focus:ring-1 focus:ring-teal-500/20 focus:outline-none text-sm transition-colors [color-scheme:dark]"
                  placeholder="End date"
                />
              </div>
              {(startDate || endDate) && (
                <button
                  type="button"
                  onClick={clearDateFilters}
                  className="text-sm text-neutral-400 hover:text-white transition-colors"
                >
                  Clear dates
                </button>
              )}
            </div>
          </div>

          <div className="md:bg-neutral-800/50 md:rounded-2xl md:border md:border-neutral-700 overflow-hidden">
            <div className="px-4 py-3 md:px-6 md:py-5 md:border-b md:border-neutral-700 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-white">
                Emails ({total})
              </h3>
              <div className="flex items-center gap-4">
                <div className="text-sm text-neutral-400 hidden md:block">
                  Page {currentPage} of {totalPages}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    void reloadEmails();
                  }}
                  className="flex items-center gap-2 bg-white text-black border-white hover:bg-gray-100 hover:text-black"
                >
                  <RefreshIcon className="fill-current size-4" />
                  <span className="hidden md:inline">Refresh</span>
                </Button>
              </div>
            </div>

            <DataTable
              data={emails}
              columns={[
                {
                  key: 'recipient',
                  header: 'Recipient',
                  accessor: (email) => (
                    <div>
                      <div className="text-white font-medium">
                        {email.recipientEmail}
                      </div>
                      {email.recipientName && (
                        <div className="text-sm text-neutral-400">
                          {email.recipientName}
                        </div>
                      )}
                    </div>
                  ),
                },
                {
                  key: 'subject',
                  header: 'Subject',
                  accessor: (email) => (
                    <div
                      className="max-w-xs truncate text-white"
                      title={email.subject}
                    >
                      {email.subject}
                    </div>
                  ),
                },
                {
                  key: 'template',
                  header: 'Template',
                  accessor: (email) => (
                    <span className="text-sm text-neutral-400">
                      {email.templateName}
                    </span>
                  ),
                },
                {
                  key: 'status',
                  header: 'Status',
                  accessor: (email) => (
                    <div>
                      <TypedStatusBadge type="email" status={email.status} />
                      {email.retryCount > 0 && (
                        <span className="ml-2 text-xs text-neutral-400">
                          ({email.retryCount} retries)
                        </span>
                      )}
                    </div>
                  ),
                },
                {
                  key: 'sent',
                  header: 'Date',
                  accessor: (email) => (
                    <span className="text-sm text-neutral-400">
                      {email.sentAt
                        ? formatShortDate(email.sentAt)
                        : email.failedAt
                          ? formatShortDate(email.failedAt)
                          : '-'}
                    </span>
                  ),
                },
                {
                  key: 'created',
                  header: 'Created',
                  accessor: (email) => (
                    <span className="text-sm text-neutral-400">
                      {formatShortDate(email.created)}
                    </span>
                  ),
                },
                {
                  key: 'actions',
                  header: 'Actions',
                  accessor: (email) => (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedEmail(email);
                      }}
                      className="text-teal-400 hover:text-teal-300 transition-colors"
                      title="View details"
                    >
                      <InfoIcon className="fill-current size-4" />
                    </button>
                  ),
                },
              ]}
              isLoading={isLoading}
              emptyMessage="No emails found"
              onRowClick={(email) => setSelectedEmail(email)}
              renderMobileItem={(email) => (
                <div className="space-y-3">
                  <div className="flex justify-between items-start gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium text-white truncate">
                        {email.subject}
                      </div>
                      <div className="text-xs text-neutral-400 mt-0.5">
                        To: {email.recipientEmail}
                      </div>
                    </div>
                    <TypedStatusBadge type="email" status={email.status} />
                  </div>

                  <div className="flex justify-between items-center text-xs text-neutral-400">
                    <div>{email.templateName}</div>
                    <div>
                      {email.sentAt
                        ? formatShortDate(email.sentAt)
                        : email.failedAt
                          ? formatShortDate(email.failedAt)
                          : formatShortDate(email.created)}
                    </div>
                  </div>
                </div>
              )}
            />

            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          </div>
        </div>
      </div>

      <EmailDetailModal
        email={selectedEmail}
        isOpen={!!selectedEmail}
        onClose={() => setSelectedEmail(null)}
      />
    </>
  );
}
